import { useRef, useMemo, useEffect, forwardRef, Suspense } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { Html, Stars, useTexture } from "@react-three/drei";
import * as THREE from "three";

import { gsap } from "@/lib/gsap";
import { CITIES, latLngToVec3, arcCurve } from "@/lib/coordinates";

// Globe route on every side: the long distance, Tokyo to Noida to Jaipur.
// (Patna/Kanpur stay in coordinates.js; swap here to bring them back.)
const ROUTE = { from: "tokyo", via: "noida", lift: 1.3, long: true };

// Long route: start with the first city (Tokyo) facing the camera, then turn
// the globe to India while the arc travels. Short route: the old quick turn.
const T = ROUTE.long
  ? { turnAt: 0.4, turn: 3.0, fromPop: 0.2, arcA: 0.6, arcADur: 2.6, viaPop: 3.2, arcB: 3.4, arcBDur: 0.6, later: 0.8 }
  : { turnAt: 0, turn: 1.8, fromPop: 0.7, arcA: 1.0, arcADur: 1.1, viaPop: 2.0, arcB: 2.3, arcBDur: 1.1, later: 0 };

/**
 * EarthTransition
 * -----------------------------------------------------------
 * Renders a 3D globe using @react-three/fiber + drei.
 * A GSAP timeline orchestrates:
 *   1. Rotate globe so South Asia faces the camera
 *   2. Animate gold arcs Tokyo → Noida → Jaipur
 *   3. Pop city markers in sequence
 *   4. Dolly-zoom camera aggressively into the Jaipur marker
 *   5. Ivory-flash fade-out → onComplete()
 */
const R = 1;

export default function EarthTransition({ onComplete }) {
  return (
    <section className="relative w-full h-full overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 35%, #1F1B4D 0%, #12113A 45%, #0A0B26 100%)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "url(/earth/night-sky.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.6,
          mixBlendMode: "screen",
        }}
      />
      <TwinklingStars />
      <Moon />

      <Canvas
        className="absolute inset-0"
        camera={{ position: [0, 0, R * 4.3], fov: 45, near: 0.1, far: 1000 }}
        dpr={[1, 2]}
        gl={{ antialias: true, alpha: true }}
      >
        <Suspense fallback={null}>
          <Scene onComplete={onComplete} />
        </Suspense>
      </Canvas>

      {/* Caption */}
      <div
        id="earth-caption"
        className="absolute bottom-[10vh] left-1/2 -translate-x-1/2 font-display italic text-ivory opacity-0 z-[3] whitespace-nowrap"
        style={{ fontSize: "clamp(20px, 2.4vw, 32px)" }}
      >
        Where two stories… <span className="text-gold-light">become one</span>
      </div>

      {/* Ivory flash used to transition to hero */}
      <div
        id="earth-flash"
        className="absolute inset-0 bg-ivory opacity-0 pointer-events-none z-[4]"
      />
    </section>
  );
}

/* ----------- Sky: twinkling stars + moon (DOM layers behind the globe) ----------- */
function TwinklingStars({ count = 90 }) {
  // Fixed random layout per mount; each star pulses on its own rhythm
  const stars = useMemo(
    () =>
      Array.from({ length: count }, () => ({
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: Math.random() < 0.15 ? 2.5 + Math.random() * 1.5 : 1 + Math.random() * 1.4,
        duration: 2 + Math.random() * 4,
        delay: Math.random() * 5,
      })),
    [count]
  );
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      {stars.map((st, i) => (
        <span
          key={i}
          className="absolute rounded-full"
          style={{
            top: `${st.top}%`,
            left: `${st.left}%`,
            width: st.size,
            height: st.size,
            background: "#FFF8E7",
            boxShadow: `0 0 ${st.size * 3}px rgba(255,248,231,0.9)`,
            animation: `star-twinkle ${st.duration}s ease-in-out ${st.delay}s infinite`,
          }}
        />
      ))}
      <style>{`
        @keyframes star-twinkle {
          0%, 100% { opacity: 0.25; transform: scale(0.8); }
          50%      { opacity: 1;    transform: scale(1.25); }
        }
      `}</style>
    </div>
  );
}

function Moon() {
  return (
    <div
      className="absolute pointer-events-none rounded-full top-[9%] right-[8%] w-16 h-16 sm:w-24 sm:h-24"
      aria-hidden="true"
      style={{
        // soft full moon with faint "maria" patches
        background:
          "radial-gradient(circle at 62% 38%, rgba(214,206,186,0.55) 0 9%, transparent 10%)," +
          "radial-gradient(circle at 38% 62%, rgba(214,206,186,0.45) 0 12%, transparent 13%)," +
          "radial-gradient(circle at 30% 34%, rgba(214,206,186,0.35) 0 6%, transparent 7%)," +
          "radial-gradient(circle at 40% 35%, #FFFDF4 0%, #F4EBD0 60%, #E2D4B0 100%)",
        boxShadow:
          "0 0 30px 8px rgba(255,246,214,0.35), 0 0 90px 30px rgba(190,200,255,0.18)",
      }}
    />
  );
}

/* ----------- 3D Scene ----------- */
function Scene({ onComplete }) {
  const { camera } = useThree();
  const globeRef = useRef();
  const atmoRef = useRef();
  const markerFromRef = useRef();
  const markerViaRef = useRef();
  const arcARef = useRef();

  const [dayMap, nightMap, bumpMap] = useTexture([
    "/earth/earth-blue-marble.jpg",
    "/earth/earth-night.jpg",
    "/earth/earth-topology.png",
  ]);
  dayMap.colorSpace = THREE.SRGBColorSpace;
  nightMap.colorSpace = THREE.SRGBColorSpace;

  // Pre-compute city positions and arcs
  const { pFrom, pVia, pJaipur, arcA } = useMemo(() => {
    const from = CITIES[ROUTE.from];
    const via = CITIES[ROUTE.via];
    const pFrom = latLngToVec3(from.lat, from.lng);
    const pVia = latLngToVec3(via.lat, via.lng);
    const pJaipur = latLngToVec3(CITIES.jaipur.lat, CITIES.jaipur.lng);
    // a long distance arc needs more lift so it clears the globe
    const arcA = arcCurve(pFrom, pVia, ROUTE.lift).getPoints(80);
    return { pFrom, pVia, pJaipur, arcA };
  }, []);

  // No idle spin: keeps Jaipur centred for the camera dolly.

  // GSAP timeline — runs once on mount
  useEffect(() => {
    if (!globeRef.current) return;

    // Exact rotation that brings Jaipur to the front (+Z) of the globe.
    // Euler "XYZ": Ry is applied first (swing Jaipur onto the x=0 meridian),
    // then Rx (tilt it down onto the equator line facing the camera).
    const ry = Math.atan2(-pJaipur.x, pJaipur.z);
    const rx = Math.atan2(pJaipur.y, Math.hypot(pJaipur.x, pJaipur.z));

    if (ROUTE.long) {
      // same maths, aimed at the first city so it starts front and centre.
      // Take the short way round (Tokyo to India is ~64 degrees west), not
      // the ~296 degree spin atan2's wrap would otherwise give.
      let fy = Math.atan2(-pFrom.x, pFrom.z);
      const d = Math.atan2(Math.sin(fy - ry), Math.cos(fy - ry));
      fy = ry + d;
      globeRef.current.rotation.y = fy;
      globeRef.current.rotation.x = Math.atan2(pFrom.y, Math.hypot(pFrom.x, pFrom.z));
    } else {
      // Start a little west/south so the globe visibly turns into place
      globeRef.current.rotation.x = rx - 0.25;
      globeRef.current.rotation.y = ry - 0.7;
    }

    const tl = gsap.timeline({ onComplete });

    // Rotate into final India framing
    tl.to(globeRef.current.rotation, { x: rx, y: ry, duration: T.turn, ease: "power2.inOut" }, T.turnAt);

    // Pop the first city
    tl.to(
      markerFromRef.current.scale,
      { x: 1, y: 1, z: 1, duration: 0.4, ease: "back.out(2)" },
      T.fromPop
    );


    // Draw arc A
    tl.to(arcARef.current.material.uniforms.uProgress, {
      value: 1,
      duration: T.arcADur,
      ease: "power1.inOut",
    }, T.arcA);

    // Pop the second city
    tl.to(
      markerViaRef.current.scale,
      { x: 1, y: 1, z: 1, duration: 0.4, ease: "back.out(2)" },
      T.viaPop
    );




    tl.fromTo(
      "#earth-caption",
      { opacity: 0, y: 10 },
      { opacity: 1, y: 0, duration: 0.7 },
      3.4 + T.later
    );

    // Dolly-zoom into Jaipur
    // Jaipur now sits on the +Z axis, so dolly straight in along Z
    tl.to(camera.position, {
      x: 0,
      y: 0,
      z: 1.18,
      duration: 2.0,
      ease: "power3.in",
      onUpdate: () => camera.updateProjectionMatrix(),
    }, 4.2 + T.later);

    tl.to("#earth-caption", { opacity: 0, duration: 0.5 }, 5.8 + T.later);


    tl.to("#earth-flash", { opacity: 1, duration: 0.4, ease: "power2.in" }, 6.0 + T.later);

    return () => {
      tl.kill();
    };
  }, [onComplete, pFrom, pJaipur, camera]);

  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={0.35} />
      <directionalLight position={[-5, 3, 5]} intensity={2} color="#FFE7C2" />

      {/* Starfield */}
      <Stars radius={300} depth={60} count={9000} factor={4.5} saturation={0} fade speed={0.5} />

      {/* Globe group: earth + arcs + markers rotate together */}
      <group ref={globeRef}>
      <mesh>
        <sphereGeometry args={[R, 96, 96]} />
        <meshStandardMaterial
          map={dayMap}
          bumpMap={bumpMap}
          bumpScale={0.03}
          emissiveMap={nightMap}
          emissive="#E8CC7E"
          emissiveIntensity={0.35}
          roughness={0.9}
          metalness={0.05}
        />
      </mesh>

      {/* Arcs */}
      <AnimatedArc ref={arcARef} points={arcA} color="#E8CC7E" />

      {/* City Markers */}
      <CityMarker ref={markerFromRef} position={pFrom} color="#E8CC7E" />
      <CityMarker ref={markerViaRef} position={pVia} color="#E91E63" />
      {/* city names beside the pins (Jaipur has no pin; the camera dives into it) */}
      <CityLabel position={pFrom} text={CITIES[ROUTE.from].label} showAt={T.fromPop + 0.1} hideAt={4.2 + T.later} side="left" />
      <CityLabel position={pVia} text={CITIES[ROUTE.via].label} showAt={T.viaPop + 0.1} hideAt={4.2 + T.later} />
      </group>

      {/* Atmosphere (fresnel glow) */}
      <mesh ref={atmoRef}>
        <sphereGeometry args={[R * 1.04, 64, 64]} />
        <shaderMaterial
          side={THREE.BackSide}
          blending={THREE.AdditiveBlending}
          transparent
          depthWrite={false}
          vertexShader={ATMO_VERT}
          fragmentShader={ATMO_FRAG}
        />
      </mesh>

    </>
  );
}

const ATMO_VERT = `
  varying vec3 vNormal;
  void main() {
    vNormal = normalize(normalMatrix * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const ATMO_FRAG = `
  varying vec3 vNormal;
  void main() {
    float intensity = pow(0.7 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 2.5);
    gl_FragColor = vec4(0.68, 0.72, 1.0, 1.0) * clamp(intensity, 0.0, 1.0);
  }
`;

/* ----------- Animated Arc (draw-on effect via shader uProgress) ----------- */
const AnimatedArc = forwardRef(function AnimatedArc({ points, color }, ref) {
  // We draw the line with a custom shader that clips based on a uniform
  // `uProgress` from 0 → 1. This gives us a nice "draw-on" reveal we can
  // animate with GSAP.
  const { geometry, material } = useMemo(() => {
    const positions = new Float32Array(points.length * 3);
    const progresses = new Float32Array(points.length);
    points.forEach((p, i) => {
      positions[i * 3]     = p.x;
      positions[i * 3 + 1] = p.y;
      positions[i * 3 + 2] = p.z;
      progresses[i] = i / (points.length - 1);
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.setAttribute("aProgress", new THREE.BufferAttribute(progresses, 1));

    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uProgress: { value: 0 },
        uColor: { value: new THREE.Color(color) },
      },
      vertexShader: `
        attribute float aProgress;
        varying float vProgress;
        void main() {
          vProgress = aProgress;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uProgress;
        uniform vec3 uColor;
        varying float vProgress;
        void main() {
          if (vProgress > uProgress) discard;
          // soft leading edge
          float edge = smoothstep(uProgress - 0.08, uProgress, vProgress);
          gl_FragColor = vec4(uColor, 1.0 - edge * 0.3);
        }
      `,
      transparent: true,
    });
    return { geometry: geo, material: mat };
  }, [points, color]);

  return <line ref={ref} geometry={geometry} material={material} />;
});

/* ----------- City name next to a pin (DOM label that follows the globe) -----------
   Timed with CSS animations, not the GSAP timeline: drei's Html mounts its DOM a
   frame later, so a ref would still be empty when the timeline is built. */
function CityLabel({ position, text, showAt, hideAt, side = "right" }) {
  return (
    <Html position={position.clone().multiplyScalar(1.02)} zIndexRange={[3, 0]} style={{ pointerEvents: "none" }}>
      {/* side="left" puts the name before the pin, for a pin near the screen's right edge */}
      <div
        className={`-mt-2 font-body font-bold uppercase tracking-[0.18em] text-xs md:text-sm text-gold-light whitespace-nowrap ${
          side === "left" ? "-translate-x-full -ml-2.5" : "ml-2.5"
        }`}
        style={{
          opacity: 0,
          textShadow: "0 2px 8px rgba(58,10,24,0.85)",
          animation: `city-label-in 0.5s ease ${showAt}s forwards, city-label-out 0.4s ease ${hideAt}s forwards`,
        }}
      >
        {text}
        <style>{`
          @keyframes city-label-in { from { opacity: 0 } to { opacity: 1 } }
          @keyframes city-label-out { from { opacity: 1 } to { opacity: 0 } }
        `}</style>
      </div>
    </Html>
  );
}

/* ----------- City Marker (dot + ring) ----------- */
const CityMarker = forwardRef(function CityMarker({ position, color, larger = false }, ref) {
  const ringRef = useRef();
  useEffect(() => {
    // Orient the ring to face outward from the globe center
    if (ringRef.current) {
      ringRef.current.lookAt(position.clone().multiplyScalar(2));
    }
  }, [position]);

  return (
    <group ref={ref} position={position} scale={[0, 0, 0]}>
      <mesh>
        <sphereGeometry args={[larger ? 0.014 : 0.01, 16, 16]} />
        <meshBasicMaterial color={color} />
      </mesh>
      <mesh ref={ringRef}>
        <ringGeometry args={[0.018, 0.024, 32]} />
        <meshBasicMaterial color={color} transparent opacity={0.8} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
});
